import type { ReactNode } from "react";
import { useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { Box, Button, Chip, Typography } from "@mui/material";
import { baseTheme } from "../../../themes/shipinsure";
import { CLAIM_STATUS, CARE_EMAIL } from "../constants";
import { statusColors } from "../statusStyles";
import type { ClaimTranslator, HeroContent, NextSteps } from "../types";

type StatusStyle = {
  iconColor: string;
  chipBackground: string;
  chipColor: string;
};

const STATUS_STYLES: Record<string, StatusStyle> = {
  [CLAIM_STATUS.PENDING]: {
    iconColor: statusColors.pendingIcon,
    chipBackground: statusColors.pendingChipBg,
    chipColor: statusColors.pendingChipText,
  },
  [CLAIM_STATUS.NEW]: {
    iconColor: statusColors.pendingIcon,
    chipBackground: statusColors.pendingChipBg,
    chipColor: statusColors.pendingChipText,
  },
  [CLAIM_STATUS.DENIED]: {
    iconColor: baseTheme.colors.error,
    chipBackground: statusColors.deniedChipBg,
    chipColor: statusColors.deniedChipText,
  },
  default: {
    iconColor: statusColors.successIcon,
    chipBackground: statusColors.successChipBg,
    chipColor: statusColors.successChipText,
  },
};

const visuallyHiddenSx = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

type StatusHeroProps = {
  hero: HeroContent;
  claimNumber: string;
  requestType: string;
  nextSteps: NextSteps | null;
  notificationEmail: string | null;
  statusAnnouncement: string;
  t: ClaimTranslator;
};

export const StatusHero = ({
  hero,
  claimNumber,
  requestType,
  nextSteps,
  notificationEmail,
  statusAnnouncement,
  t,
}: StatusHeroProps) => {
  const [copied, setCopied] = useState(false);
  const isDenied = hero.key === CLAIM_STATUS.DENIED;
  const isAwaitingReview =
    hero.key === CLAIM_STATUS.PENDING || hero.key === CLAIM_STATUS.NEW;
  const styles = STATUS_STYLES[hero.key] ?? STATUS_STYLES.default;
  const Icon = isDenied
    ? ErrorOutlineIcon
    : isAwaitingReview
      ? FactCheckOutlinedIcon
      : CheckCircleOutlineIcon;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(claimNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box mb="16px" position="relative">
      <Typography component="p" sx={visuallyHiddenSx} aria-live="polite">
        {statusAnnouncement}
      </Typography>

      <Box
        display="flex"
        alignItems="flex-start"
        gap="12px"
        mb={nextSteps ? "12px" : 0}
      >
        <Icon
          sx={{ fontSize: 36, color: styles.iconColor, mt: "2px" }}
          aria-hidden
        />
        <Box flex={1} minWidth={0}>
          <Typography
            variant="h4"
            fontWeight={700}
            mb="6px"
            sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" } }}
          >
            {hero.title}
          </Typography>
          <Typography color={statusColors.metaText} mb="10px" fontSize="15px">
            {hero.description}
          </Typography>
          <Chip
            label={t("status.statusChip", { status: hero.statusLabel })}
            sx={{
              backgroundColor: styles.chipBackground,
              color: styles.chipColor,
              fontWeight: 700,
              fontSize: "14px",
              mb: "10px",
            }}
          />
          <Box display="flex" alignItems="center" flexWrap="wrap" gap="6px">
            <Typography fontSize="14px" color={statusColors.metaText}>
              {t("status.claimNumber", { requestType })}:{" "}
              <Box
                component="span"
                fontWeight={600}
                color={baseTheme.colors.text}
              >
                {claimNumber}
              </Box>
            </Typography>
            <Button
              size="small"
              onClick={handleCopy}
              startIcon={
                <ContentCopyIcon sx={{ fontSize: "14px !important" }} />
              }
              aria-label={t("status.copyClaimNumberAria", { requestType })}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                color: statusColors.metaText,
                minWidth: 0,
                px: 1,
                py: 0.25,
              }}
            >
              {copied
                ? t("status.claimNumberCopied")
                : t("status.copyClaimNumber")}
            </Button>
          </Box>
        </Box>
      </Box>

      {nextSteps ? (
        <Box
          sx={{
            border: `1px solid ${statusColors.panelBorder}`,
            borderRadius: "12px",
            padding: "16px",
            backgroundColor: statusColors.panelMutedBg,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap="8px"
            mb="8px"
          >
            <Typography fontWeight={700} fontSize="16px">
              {nextSteps.title}
            </Typography>
            <NextStepsBadge nextSteps={nextSteps} t={t} />
          </Box>
          {nextSteps.timeframe ? (
            <Typography fontSize="15px" color={baseTheme.colors.text} mb="6px">
              {nextSteps.timeframe}
            </Typography>
          ) : null}
          <Typography
            fontSize="15px"
            color={statusColors.metaText}
            mb={notificationEmail ? "6px" : 0}
          >
            {nextSteps.body}
          </Typography>
          {notificationEmail ? (
            <Typography fontSize="14px" color={statusColors.metaText}>
              {t("status.updatesSentTo", {
                email: notificationEmail,
                fromEmail: CARE_EMAIL,
              })}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

const NextStepsBadge = ({
  nextSteps,
  t,
}: {
  nextSteps: NextSteps;
  t: ClaimTranslator;
}) => {
  if (nextSteps.actionRequired) {
    return (
      <Chip
        size="small"
        label={t("status.actionNeededBadge")}
        sx={{
          backgroundColor: statusColors.pendingChipBg,
          color: statusColors.pendingChipText,
          fontWeight: 700,
          fontSize: "13px",
        }}
      />
    );
  }

  if (!nextSteps.showNoActionBadge) {
    return null;
  }

  return (
    <Chip
      size="small"
      label={t("status.noActionNeededBadge")}
      sx={{
        backgroundColor: statusColors.successChipBg,
        color: statusColors.successChipText,
        fontWeight: 700,
        fontSize: "13px",
      }}
    />
  );
};
